import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Alert, Button, Form, Modal, OverlayTrigger, Table, Tooltip} from 'react-bootstrap'
import Select, {SingleValue} from 'react-select'
import AsyncSelect from 'react-select/async'
import Swal from 'sweetalert2'
import {
  salesforceApiService,
  SalesforceAccountMapping,
  SalesforceAccountOption,
  SalesforceImportPreview,
  SalesforceQueue,
} from '../services/salesforceApi'
import {SelectAutoComplete} from '../../../../_metronic/partials/components/Select/SelectAutoComplete'

interface AccountMappingForm {
  id: number | null
  accountId: string
  accountName: string
  projectId: number | null
  projectName: string
}

interface QueueMappingForm {
  localKey: number
  queueId: string
  queueName: string
  projectId: number | null
  projectName: string
}

interface SalesforceManageModalProps {
  show: boolean
  onClose: () => void
  onSuccess: () => void
}

const swalThemeBackground = () =>
  (window.localStorage.getItem('kt_theme_mode_value') || '') === 'dark' ? '#353b48' : '#fff'

const toFormMappings = (mappingsData: SalesforceAccountMapping[]): AccountMappingForm[] =>
  mappingsData.map((m) => ({
    id: m.id,
    accountId: m.account_id,
    accountName: m.account_name || m.account_id,
    projectId: m.project_id,
    projectName: m.project_name || '',
  }))

const normalizeForm = (rows: AccountMappingForm[]) =>
  rows
    .map((m) => ({
      id: m.id && m.id > 0 ? m.id : null,
      accountId: m.accountId,
      projectId: m.projectId,
    }))
    .sort((a, b) => {
      const aKey = `${a.id ?? 'new'}-${a.accountId}`
      const bKey = `${b.id ?? 'new'}-${b.accountId}`
      return aKey.localeCompare(bKey)
    })

const toQueueFormMappings = (queuesData: SalesforceQueue[]): QueueMappingForm[] =>
  queuesData
    .filter((q) => q.is_enabled)
    .map((q, index) => ({
      localKey: index + 1,
      queueId: q.queue_id,
      queueName: q.queue_name,
      projectId: q.project_id,
      projectName: q.project_name || '',
    }))

const normalizeQueueForm = (rows: QueueMappingForm[]) =>
  rows
    .map((m) => ({queueId: m.queueId, projectId: m.projectId}))
    .sort((a, b) => a.queueId.localeCompare(b.queueId))

type AccountSelectOption = {value: string; label: string}

const toSelectOptions = (
  accounts: SalesforceAccountOption[],
  excludedAccountIds: string[],
  currentValue: AccountSelectOption | null
): AccountSelectOption[] => {
  const excluded = new Set(excludedAccountIds)
  const options = accounts
    .filter((account) => !excluded.has(account.account_id))
    .map((account) => ({
      value: account.account_id,
      label: account.account_name,
    }))

  if (currentValue && !options.some((o) => o.value === currentValue.value)) {
    options.unshift(currentValue)
  }

  return options
}

const toQueueSelectOptions = (
  allQueues: SalesforceQueue[],
  excludedQueueIds: string[],
  currentValue: AccountSelectOption | null
): AccountSelectOption[] => {
  const excluded = new Set(excludedQueueIds)
  const options = allQueues
    .filter((queue) => !excluded.has(queue.queue_id))
    .map((queue) => ({
      value: queue.queue_id,
      label: queue.queue_name,
    }))

  if (currentValue && !options.some((o) => o.value === currentValue.value)) {
    options.unshift(currentValue)
  }

  return options
}

const selectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999}),
  valueContainer: (styles: Record<string, unknown>) => ({
    ...styles,
    padding: '2px 8px',
  }),
  input: (styles: Record<string, unknown>) => ({
    ...styles,
    margin: '0px',
    padding: '0px',
  }),
  option: (
    styles: Record<string, unknown>,
    {isDisabled, isFocused, isSelected}: {isDisabled: boolean; isFocused: boolean; isSelected: boolean}
  ) => ({
    ...styles,
    backgroundColor: isDisabled
      ? undefined
      : isSelected
        ? '#b5d44e70'
        : isFocused
          ? '#b5d44e70'
          : undefined,
    color: isDisabled ? '#ccc' : isSelected || isFocused ? '#181C32' : undefined,
  }),
}

const selectClassNames = {
  control: () => 'form-control form-control-solid py-1',
  input: () => 'text-dark',
  singleValue: () => 'text-dark',
  menu: () => 'bg-body',
}

interface SalesforceAccountSelectProps {
  mappingId: number | null
  value: AccountSelectOption | null
  excludedAccountIds: string[]
  defaultAccounts: SalesforceAccountOption[]
  isDisabled?: boolean
  onChange: (option: SingleValue<AccountSelectOption>) => void
}

const SalesforceAccountSelect: FC<SalesforceAccountSelectProps> = ({
  mappingId,
  value,
  excludedAccountIds,
  defaultAccounts,
  isDisabled,
  onChange,
}) => {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestIdRef = useRef(0)
  const excludedRef = useRef(excludedAccountIds)
  const valueRef = useRef(value)

  useEffect(() => {
    excludedRef.current = excludedAccountIds
  }, [excludedAccountIds])

  useEffect(() => {
    valueRef.current = value
  }, [value])

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const defaultOptions = useMemo(
    () => toSelectOptions(defaultAccounts, excludedAccountIds, value),
    [defaultAccounts, excludedAccountIds, value]
  )

  const loadOptions = useCallback((inputValue: string): Promise<AccountSelectOption[]> => {
    const query = inputValue.trim()

    // Empty query: reuse the shared initial list (no extra request)
    if (query === '') {
      return Promise.resolve(
        toSelectOptions(defaultAccounts, excludedRef.current, valueRef.current)
      )
    }

    return new Promise((resolve) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }

      const requestId = ++requestIdRef.current

      debounceRef.current = setTimeout(async () => {
        try {
          const accounts = await salesforceApiService.searchAccounts(query)
          if (requestId !== requestIdRef.current) {
            resolve([])
            return
          }

          resolve(toSelectOptions(accounts, excludedRef.current, valueRef.current))
        } catch (err) {
          console.error('Erro ao buscar Accounts:', err)
          resolve([])
        }
      }, 400)
    })
  }, [defaultAccounts])

  return (
    <AsyncSelect
      key={`account-select-${mappingId}`}
      cacheOptions={false}
      defaultOptions={defaultOptions}
      loadOptions={loadOptions}
      value={value}
      onChange={onChange}
      placeholder={
        isDisabled && defaultAccounts.length === 0
          ? 'Carregando Accounts...'
          : 'Buscar Account...'
      }
      isDisabled={isDisabled}
      isClearable
      isSearchable
      noOptionsMessage={({inputValue}) =>
        inputValue ? 'Nenhum Account encontrado' : 'Digite para buscar Accounts'
      }
      loadingMessage={() => 'Buscando...'}
      menuPortalTarget={document.body}
      menuPosition='fixed'
      styles={selectStyles}
      classNames={selectClassNames}
    />
  )
}

interface SalesforceQueueSelectProps {
  mappingKey: number
  value: AccountSelectOption | null
  excludedQueueIds: string[]
  availableQueues: SalesforceQueue[]
  isDisabled?: boolean
  isLoading?: boolean
  onChange: (option: SingleValue<AccountSelectOption>) => void
}

const SalesforceQueueSelect: FC<SalesforceQueueSelectProps> = ({
  mappingKey,
  value,
  excludedQueueIds,
  availableQueues,
  isDisabled,
  isLoading,
  onChange,
}) => {
  const options = useMemo(
    () => toQueueSelectOptions(availableQueues, excludedQueueIds, value),
    [availableQueues, excludedQueueIds, value]
  )

  return (
    <Select
      key={`queue-select-${mappingKey}`}
      options={options}
      value={value}
      onChange={onChange}
      placeholder={isLoading ? 'Carregando filas...' : 'Selecione uma fila...'}
      isDisabled={isDisabled || isLoading}
      isClearable
      isSearchable
      noOptionsMessage={() => 'Nenhuma fila disponível'}
      menuPortalTarget={document.body}
      menuPosition='fixed'
      styles={selectStyles}
      classNames={selectClassNames}
    />
  )
}

const SalesforceManageModal: FC<SalesforceManageModalProps> = ({show, onClose, onSuccess}) => {
  const [mappings, setMappings] = useState<AccountMappingForm[]>([])
  const [existingMappings, setExistingMappings] = useState<SalesforceAccountMapping[]>([])
  const [savedSnapshot, setSavedSnapshot] = useState<AccountMappingForm[]>([])
  const [defaultAccounts, setDefaultAccounts] = useState<SalesforceAccountOption[]>([])
  const [importPreview, setImportPreview] = useState<SalesforceImportPreview | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [availableQueues, setAvailableQueues] = useState<SalesforceQueue[]>([])
  const [queueMappings, setQueueMappings] = useState<QueueMappingForm[]>([])
  const [savedQueueSnapshot, setSavedQueueSnapshot] = useState<QueueMappingForm[]>([])
  const [isLoadingQueues, setIsLoadingQueues] = useState(false)
  const [queueError, setQueueError] = useState<string | null>(null)
  const [isSubmittingQueues, setIsSubmittingQueues] = useState(false)
  const [nextQueueLocalKey, setNextQueueLocalKey] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nextId, setNextId] = useState(1)

  const applyLoadedMappings = (mappingsData: SalesforceAccountMapping[]) => {
    setExistingMappings(mappingsData)
    const formMappings = toFormMappings(mappingsData)
    setMappings(formMappings)
    setSavedSnapshot(formMappings)

    if (formMappings.length > 0) {
      const maxId = Math.max(...formMappings.map((m) => m.id || 0))
      setNextId(maxId + 1)
    } else {
      setNextId(1)
    }
  }

  const applyLoadedQueues = (queuesData: SalesforceQueue[]) => {
    setAvailableQueues(queuesData)
    const formMappings = toQueueFormMappings(queuesData)
    setQueueMappings(formMappings)
    setSavedQueueSnapshot(formMappings)
    setNextQueueLocalKey(formMappings.length + 1)
  }

  const loadImportPreview = useCallback(async () => {
    setIsLoadingPreview(true)
    setPreviewError(null)
    try {
      const preview = await salesforceApiService.getImportPreview()
      setImportPreview(preview)
    } catch (err: unknown) {
      console.error('Erro ao carregar preview Salesforce:', err)
      setImportPreview(null)
      setPreviewError(
        err instanceof Error ? err.message : 'Não foi possível carregar o preview dos Cases de hoje.'
      )
    } finally {
      setIsLoadingPreview(false)
    }
  }, [])

  const loadQueues = useCallback(async () => {
    setIsLoadingQueues(true)
    setQueueError(null)
    try {
      const queuesData = await salesforceApiService.getQueues()
      applyLoadedQueues(queuesData)
    } catch (err: unknown) {
      console.error('Erro ao carregar filas Salesforce:', err)
      setQueueError(err instanceof Error ? err.message : 'Não foi possível carregar as filas.')
    } finally {
      setIsLoadingQueues(false)
    }
  }, [])

  const loadAccountsInBackground = useCallback(async () => {
    setIsLoadingAccounts(true)
    try {
      const accountsData = await salesforceApiService.searchAccounts('')
      setDefaultAccounts(accountsData)
    } catch (err) {
      console.error('Erro ao carregar Accounts do Salesforce:', err)
      // Keep modal usable; search-on-type still hits the backend.
    } finally {
      setIsLoadingAccounts(false)
    }
  }, [])

  const loadData = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Open fast: only local mappings are required. Accounts/preview load after.
      const mappingsData = await salesforceApiService.getAccountMappings()
      applyLoadedMappings(mappingsData)
      setIsLoading(false)
      void loadAccountsInBackground()
      void loadImportPreview()
      void loadQueues()
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Erro ao carregar mapeamentos Salesforce'
      setError(message)
      setIsLoading(false)
    }
  }, [loadAccountsInBackground, loadImportPreview, loadQueues])

  useEffect(() => {
    if (show) {
      loadData()
    }
  }, [show, loadData])

  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(normalizeForm(mappings)) !== JSON.stringify(normalizeForm(savedSnapshot))
  }, [mappings, savedSnapshot])

  const hasIncompleteRows = useMemo(
    () => mappings.some((m) => Boolean(m.accountId) !== Boolean(m.projectId)),
    [mappings]
  )

  const savedMappingsCount = useMemo(
    () => savedSnapshot.filter((m) => m.accountId && m.projectId && m.id && m.id > 0).length,
    [savedSnapshot]
  )

  const hasUnsavedQueueChanges = useMemo(() => {
    return (
      JSON.stringify(normalizeQueueForm(queueMappings)) !==
      JSON.stringify(normalizeQueueForm(savedQueueSnapshot))
    )
  }, [queueMappings, savedQueueSnapshot])

  const hasIncompleteQueueRows = useMemo(
    () => queueMappings.some((m) => Boolean(m.queueId) !== Boolean(m.projectId)),
    [queueMappings]
  )

  const savedQueueMappingsCount = useMemo(
    () => savedQueueSnapshot.filter((m) => m.queueId && m.projectId).length,
    [savedQueueSnapshot]
  )

  // One unified save button persists both tables, so both lock together while either
  // save is in flight.
  const isSavingAny = isSubmitting || isSubmittingQueues

  // Account mapping and Queue mapping work independently: either one alone is enough
  // to allow importing — neither depends on the other being configured.
  const importDisabledReason = useMemo(() => {
    if (isLoading) return 'Aguarde o carregamento dos mapeamentos.'
    if (isSavingAny) return 'Aguarde o salvamento dos mapeamentos.'
    if (isImporting) return 'Importação em andamento.'
    if (hasUnsavedChanges || hasUnsavedQueueChanges) {
      return 'Há alterações não salvas no mapeamento. Salve antes de importar.'
    }
    if (hasIncompleteRows) {
      return 'Há linhas incompletas no mapeamento de Account. Preencha Account e Projeto ou remova a linha.'
    }
    if (hasIncompleteQueueRows) {
      return 'Há linhas incompletas no mapeamento de Fila. Preencha Fila e Projeto ou remova a linha.'
    }
    if (savedMappingsCount === 0 && savedQueueMappingsCount === 0) {
      return 'Configure e salve ao menos um mapeamento Account → Projeto ou Fila → Projeto antes de importar.'
    }
    return null
  }, [
    isLoading,
    isSavingAny,
    isImporting,
    hasUnsavedChanges,
    hasIncompleteRows,
    hasUnsavedQueueChanges,
    hasIncompleteQueueRows,
    savedMappingsCount,
    savedQueueMappingsCount,
  ])

  const canImport = importDisabledReason === null

  const handleClose = () => {
    setError(null)
    setMappings([])
    setExistingMappings([])
    setSavedSnapshot([])
    setDefaultAccounts([])
    setImportPreview(null)
    setPreviewError(null)
    setIsLoadingAccounts(false)
    setIsLoadingPreview(false)
    setAvailableQueues([])
    setQueueMappings([])
    setSavedQueueSnapshot([])
    setQueueError(null)
    setIsLoadingQueues(false)
    onClose()
  }

  const handleAddMapping = () => {
    const newMapping: AccountMappingForm = {
      id: -nextId,
      accountId: '',
      accountName: '',
      projectId: null,
      projectName: '',
    }
    setMappings([...mappings, newMapping])
    setNextId(nextId + 1)
  }

  const handleRemoveMapping = (id: number | null) => {
    setMappings(mappings.filter((m) => m.id !== id))
  }

  const handleMappingChange = (
    id: number | null,
    field: keyof AccountMappingForm,
    value: unknown
  ) => {
    setMappings(
      mappings.map((m) => {
        if (m.id !== id) return m

        if (field === 'accountId') {
          const option = value as {value: string; label: string} | null
          return {
            ...m,
            accountId: option?.value || '',
            accountName: option?.label || '',
          }
        }

        if (field === 'projectId') {
          if (
            !value ||
            (typeof value === 'object' &&
              (!('value' in (value as object)) || !(value as {value?: string}).value))
          ) {
            return {...m, projectId: null, projectName: ''}
          }

          const projectValue =
            typeof value === 'object' &&
            value !== null &&
            'value' in value &&
            (value as {value?: string}).value
              ? Number((value as {value: string}).value)
              : null
          const projectLabel =
            typeof value === 'object' && value !== null && 'label' in value
              ? String((value as {label?: string}).label || '')
              : ''

          return {
            ...m,
            projectId: projectValue && !isNaN(projectValue) ? projectValue : null,
            projectName: projectLabel,
          }
        }

        return {...m, [field]: value as never}
      })
    )
  }

  const handleAddQueueMapping = () => {
    const newMapping: QueueMappingForm = {
      localKey: nextQueueLocalKey,
      queueId: '',
      queueName: '',
      projectId: null,
      projectName: '',
    }
    setQueueMappings([...queueMappings, newMapping])
    setNextQueueLocalKey(nextQueueLocalKey + 1)
  }

  const handleRemoveQueueMapping = (localKey: number) => {
    setQueueMappings(queueMappings.filter((m) => m.localKey !== localKey))
  }

  const handleQueueMappingChange = (
    localKey: number,
    field: 'queueId' | 'projectId',
    value: unknown
  ) => {
    setQueueMappings(
      queueMappings.map((m) => {
        if (m.localKey !== localKey) return m

        if (field === 'queueId') {
          const option = value as {value: string; label: string} | null
          return {
            ...m,
            queueId: option?.value || '',
            queueName: option?.label || '',
          }
        }

        // field === 'projectId'
        const option = value as {value?: string; label?: string} | null
        if (!option?.value) {
          return {...m, projectId: null, projectName: ''}
        }

        const projectValue = Number(option.value)

        return {
          ...m,
          projectId: !isNaN(projectValue) ? projectValue : null,
          projectName: option.label || '',
        }
      })
    )
  }

  const persistMappings = async (): Promise<boolean> => {
    if (hasIncompleteRows) {
      setError('Preencha Account e Projeto em todas as linhas ou remova as incompletas.')
      return false
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const validMappings = mappings.filter((m) => m.accountId && m.projectId)
      const existingIds = mappings
        .filter((m) => m.id !== null && m.id > 0)
        .map((m) => m.id as number)
      const toDelete = existingMappings.filter((m) => !existingIds.includes(m.id))

      for (const mapping of toDelete) {
        await salesforceApiService.deleteAccountMapping(mapping.id)
      }

      for (const mapping of validMappings) {
        if (mapping.id === null || mapping.id < 0) {
          await salesforceApiService.createAccountMapping({
            account_id: mapping.accountId,
            account_name: mapping.accountName,
            project_id: mapping.projectId!,
          })
        } else {
          await salesforceApiService.updateAccountMapping(mapping.id, {
            project_id: mapping.projectId!,
            account_name: mapping.accountName,
            account_id: mapping.accountId,
          })
        }
      }

      const refreshed = await salesforceApiService.getAccountMappings()
      applyLoadedMappings(refreshed)
      onSuccess()
      void loadImportPreview()
      return true
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar mapeamentos'
      setError(message)
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  const persistQueueMappings = async (): Promise<boolean> => {
    if (hasIncompleteQueueRows) {
      setQueueError('Preencha Fila e Projeto em todas as linhas ou remova as incompletas.')
      return false
    }

    setIsSubmittingQueues(true)
    setQueueError(null)

    try {
      const validMappings = queueMappings.filter((m) => m.queueId && m.projectId)
      const currentQueueIds = validMappings.map((m) => m.queueId)
      const toDisable = availableQueues.filter(
        (q) => q.is_enabled && !currentQueueIds.includes(q.queue_id)
      )

      for (const queue of toDisable) {
        await salesforceApiService.updateQueue(queue.queue_id, {
          queue_name: queue.queue_name,
          is_enabled: false,
        })
      }

      for (const mapping of validMappings) {
        await salesforceApiService.updateQueue(mapping.queueId, {
          queue_name: mapping.queueName,
          is_enabled: true,
          project_id: mapping.projectId,
        })
      }

      const refreshed = await salesforceApiService.getQueues()
      applyLoadedQueues(refreshed)
      onSuccess()
      void loadImportPreview()
      return true
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar filas'
      setQueueError(message)
      return false
    } finally {
      setIsSubmittingQueues(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Saved together from one button, but each persists (and reports errors)
    // independently — a failure in one doesn't block or roll back the other.
    if (hasUnsavedChanges) {
      await persistMappings()
    }
    if (hasUnsavedQueueChanges) {
      await persistQueueMappings()
    }
  }

  const handleImport = async () => {
    if (!canImport || isImporting) return

    try {
      setIsImporting(true)
      setError(null)
      const result = await salesforceApiService.importCases()
      const synced = result.synced ?? 0
      const icon = synced > 0 ? 'success' : 'info'

      await Swal.fire({
        icon,
        title: synced > 0 ? 'Importação concluída' : 'Nenhum Case importado',
        html: `
          <div class="text-start">
            <p class="mb-2">${result.message}</p>
            <p class="text-muted mb-0">
              Critério: Cases criados hoje (${result.import_date ?? 'hoje'}) de Accounts mapeados
              ou de filas com Projeto configurado.
              ${result.skipped ? `<br/>Ignorados sem mapeamento: ${result.skipped}.` : ''}
            </p>
          </div>
        `,
        background: swalThemeBackground(),
        confirmButtonText: 'Entendi',
        customClass: {confirmButton: 'btn btn-info'},
        buttonsStyling: false,
      })

      void loadImportPreview()
    } catch (err: unknown) {
      const text = err instanceof Error ? err.message : 'Erro ao importar Cases'
      setError(text)
      await Swal.fire({
        icon: 'error',
        title: 'Não foi possível importar',
        text,
        background: swalThemeBackground(),
        confirmButtonText: 'Ok',
        customClass: {confirmButton: 'btn btn-danger'},
        buttonsStyling: false,
      })
    } finally {
      setIsImporting(false)
    }
  }

  const importButton = (
    <Button
      variant='primary'
      type='button'
      onClick={handleImport}
      disabled={!canImport}
      style={!canImport ? {pointerEvents: 'none'} : undefined}
    >
      {isImporting ? (
        <>
          <span className='spinner-border spinner-border-sm me-2' role='status' />
          Importando...
        </>
      ) : (
        <>
          <i className='fas fa-download me-2'></i>
          Importar Cases (hoje)
        </>
      )}
    </Button>
  )

  const renderAddMappingButton = () => (
    <Button
      variant='outline-info'
      size='sm'
      type='button'
      onClick={handleAddMapping}
      disabled={isSavingAny || isLoading || isImporting}
    >
      <i className='fas fa-plus me-2'></i>
      Adicionar mapeamento
    </Button>
  )

  const renderAddQueueMappingButton = () => (
    <Button
      variant='outline-info'
      size='sm'
      type='button'
      onClick={handleAddQueueMapping}
      disabled={isSavingAny || isLoadingQueues || isImporting}
    >
      <i className='fas fa-plus me-2'></i>
      Adicionar fila
    </Button>
  )

  return (
    <Modal show={show} onHide={handleClose} size='lg' centered backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title>Gerenciar mapeamentos Salesforce</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {isLoading && mappings.length === 0 ? (
            <div className='text-center py-5'>
              <div className='spinner-border text-info' role='status'>
                <span className='visually-hidden'>Carregando...</span>
              </div>
              <p className='text-muted mt-3 mb-0'>Carregando mapeamentos...</p>
            </div>
          ) : (
            <>
              <p className='text-muted mb-4'>
                Mapeie Accounts do Salesforce para projetos do TaskRush e salve. A sincronização
                automática importa Cases de hoje a cada 15 minutos; o botão abaixo força a
                importação agora. Digite no campo Account para buscar no Salesforce.
              </p>

              {error && (
                <Alert variant='danger' className='mb-4'>
                  <i className='fas fa-exclamation-triangle me-2'></i>
                  {error}
                </Alert>
              )}

              {hasUnsavedChanges && (
                <Alert variant='warning' className='mb-4'>
                  <i className='fas fa-exclamation-circle me-2'></i>
                  Há alterações não salvas no mapeamento de Accounts. Salve antes de importar.
                </Alert>
              )}

              {hasUnsavedQueueChanges && (
                <Alert variant='warning' className='mb-4'>
                  <i className='fas fa-exclamation-circle me-2'></i>
                  Há alterações não salvas no mapeamento de filas. Salve antes de importar.
                </Alert>
              )}

              <div className='border rounded p-3 mb-4 bg-light'>
                <div className='d-flex align-items-center justify-content-between mb-2'>
                  <h6 className='mb-0'>Cases elegíveis hoje</h6>
                  {isLoadingPreview && (
                    <span className='spinner-border spinner-border-sm text-info' role='status' />
                  )}
                </div>
                {previewError ? (
                  <p className='text-danger mb-0'>{previewError}</p>
                ) : importPreview ? (
                  <div className='d-flex flex-wrap gap-4 text-muted'>
                    <span>
                      Data: <strong className='text-dark'>{importPreview.import_date}</strong>
                    </span>
                    <span>
                      Total:{' '}
                      <strong className='text-dark'>{importPreview.total_cases_count}</strong>
                    </span>
                    <span>
                      Com mapeamento:{' '}
                      <strong className='text-dark'>{importPreview.mapped_cases_count}</strong>
                    </span>
                    <span>
                      Sem Account:{' '}
                      <strong className='text-dark'>{importPreview.unassigned_cases_count}</strong>
                    </span>
                  </div>
                ) : (
                  !isLoadingPreview && (
                    <p className='text-muted mb-0'>Preview indisponível no momento.</p>
                  )
                )}
              </div>

              {queueError && (
                <Alert variant='danger' className='mb-4'>
                  <i className='fas fa-exclamation-triangle me-2'></i>
                  {queueError}
                </Alert>
              )}

              <div className='d-flex align-items-center justify-content-between mb-4'>
                <h5 className='mb-0'>Mapeamento Fila → Projeto</h5>
                {renderAddQueueMappingButton()}
              </div>

              {isLoadingQueues && queueMappings.length === 0 ? (
                <div className='text-center py-4 text-muted'>
                  <span className='spinner-border spinner-border-sm text-info me-2' role='status' />
                  Carregando filas...
                </div>
              ) : queueMappings.length === 0 ? (
                <div className='text-center py-5 text-muted'>
                  <i className='fas fa-inbox fs-2x mb-3 d-block'></i>
                  <p>Nenhuma fila mapeada</p>
                  <p className='mb-0'>Clique em &quot;Adicionar fila&quot; para começar</p>
                </div>
              ) : (
                <>
                  <div className='table-responsive'>
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Fila Salesforce</th>
                          <th>Projeto no TaskRush</th>
                          <th style={{width: '50px'}}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {queueMappings.map((mapping) => {
                          const excludedQueueIds = queueMappings
                            .filter((m) => m.localKey !== mapping.localKey && m.queueId)
                            .map((m) => m.queueId)

                          return (
                            <tr key={mapping.localKey}>
                              <td>
                                <SalesforceQueueSelect
                                  mappingKey={mapping.localKey}
                                  value={
                                    mapping.queueId
                                      ? {value: mapping.queueId, label: mapping.queueName}
                                      : null
                                  }
                                  excludedQueueIds={excludedQueueIds}
                                  availableQueues={availableQueues}
                                  isDisabled={isSavingAny || isImporting}
                                  isLoading={isLoadingQueues}
                                  onChange={(newValue) => {
                                    handleQueueMappingChange(mapping.localKey, 'queueId', newValue)
                                  }}
                                />
                              </td>
                              <td>
                                <SelectAutoComplete
                                  endPoint='projetos'
                                  value={
                                    mapping.projectId
                                      ? {
                                          value: mapping.projectId.toString(),
                                          label: mapping.projectName || '',
                                        }
                                      : undefined
                                  }
                                  placeholder='Selecione um projeto'
                                  onChange={(newValue) => {
                                    handleQueueMappingChange(mapping.localKey, 'projectId', newValue)
                                  }}
                                  isDisabled={isSavingAny || isImporting}
                                />
                              </td>
                              <td className='text-center'>
                                <Button
                                  variant='link'
                                  className='text-danger p-0'
                                  type='button'
                                  onClick={() => handleRemoveQueueMapping(mapping.localKey)}
                                  disabled={isSavingAny || isImporting}
                                  title='Remover mapeamento'
                                >
                                  <i className='fas fa-trash'></i>
                                </Button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  </div>

                  <div className='d-flex justify-content-end mt-3 mb-4'>
                    {renderAddQueueMappingButton()}
                  </div>
                </>
              )}

              {isLoadingAccounts && (
                <Alert variant='light-info' className='mb-4 d-flex align-items-center'>
                  <span
                    className='spinner-border spinner-border-sm text-info me-3'
                    role='status'
                    aria-hidden='true'
                  />
                  <span>
                    Buscando Accounts no Salesforce... Os campos de Account ficam temporariamente
                    desabilitados.
                  </span>
                </Alert>
              )}

              <div className='d-flex align-items-center justify-content-between mb-4'>
                <h5 className='mb-0'>Mapeamento Account → Projeto</h5>
                {renderAddMappingButton()}
              </div>

              {mappings.length === 0 ? (
                <div className='text-center py-5 text-muted'>
                  <i className='fas fa-inbox fs-2x mb-3 d-block'></i>
                  <p>Nenhum mapeamento configurado</p>
                  <p className='mb-0'>
                    Clique em &quot;Adicionar mapeamento&quot; para começar
                  </p>
                </div>
              ) : (
                <>
                  <div className='table-responsive'>
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Account Salesforce</th>
                          <th>Projeto no TaskRush</th>
                          <th style={{width: '50px'}}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {mappings.map((mapping, index) => {
                          const excludedAccountIds = mappings
                            .filter((m) => m.id !== mapping.id && m.accountId)
                            .map((m) => m.accountId)

                          return (
                            <tr key={mapping.id ?? `new-${index}`}>
                              <td>
                                <SalesforceAccountSelect
                                  mappingId={mapping.id}
                                  value={
                                    mapping.accountId
                                      ? {
                                          value: mapping.accountId,
                                          label: mapping.accountName || mapping.accountId,
                                        }
                                      : null
                                  }
                                  excludedAccountIds={excludedAccountIds}
                                  defaultAccounts={defaultAccounts}
                                  onChange={(newValue) => {
                                    handleMappingChange(mapping.id, 'accountId', newValue)
                                  }}
                                  isDisabled={isSavingAny || isImporting || isLoadingAccounts}
                                />
                              </td>
                              <td>
                                <SelectAutoComplete
                                  endPoint='projetos'
                                  value={
                                    mapping.projectId
                                      ? {
                                          value: mapping.projectId.toString(),
                                          label: mapping.projectName || '',
                                        }
                                      : undefined
                                  }
                                  placeholder='Selecione um projeto'
                                  onChange={(newValue) => {
                                    handleMappingChange(mapping.id, 'projectId', newValue)
                                  }}
                                  isDisabled={isSavingAny || isImporting}
                                />
                              </td>
                              <td className='text-center'>
                                <Button
                                  variant='link'
                                  className='text-danger p-0'
                                  type='button'
                                  onClick={() => handleRemoveMapping(mapping.id)}
                                  disabled={isSavingAny || isImporting}
                                  title='Remover mapeamento'
                                >
                                  <i className='fas fa-trash'></i>
                                </Button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  </div>

                  <div className='d-flex justify-content-end mt-3'>
                    {renderAddMappingButton()}
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer className='d-flex justify-content-between flex-wrap gap-2'>
          <Button
            variant='light'
            onClick={handleClose}
            disabled={isSavingAny || isImporting}
          >
            Fechar
          </Button>

          <div className='d-flex gap-2 flex-wrap'>
            <Button
              variant='info'
              type='submit'
              disabled={
                isSavingAny ||
                isLoading ||
                isImporting ||
                (!hasUnsavedChanges && !hasUnsavedQueueChanges)
              }
            >
              {isSavingAny ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2' role='status' />
                  Salvando...
                </>
              ) : (
                'Salvar mapeamentos'
              )}
            </Button>

            {importDisabledReason ? (
              <OverlayTrigger
                placement='top'
                overlay={
                  <Tooltip id='salesforce-import-disabled-tooltip'>{importDisabledReason}</Tooltip>
                }
              >
                <span className='d-inline-block'>{importButton}</span>
              </OverlayTrigger>
            ) : (
              importButton
            )}
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default SalesforceManageModal
